import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  MessageCircle, Send, Loader2, CheckCircle2, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const ContactSupportModal = ({
  open,
  onOpenChange,
  user,
  project,
  API
}) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!subject.trim() || !message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    
    try {
      await axios.post(`${API}/support/contact`, {
        user_id: user?.id,
        user_email: user?.email,
        user_name: user?.name || user?.email?.split('@')[0],
        subject: subject.trim(),
        message: message.trim(),
        category,
        project_id: project?.id,
        project_title: project?.title,
        subscription: user?.subscription || 'free'
      });
      
      setSent(true);
      toast.success('Message sent! We\'ll get back to you soon.');
      
      // Reset after 2 seconds and close
      setTimeout(() => {
        setSubject('');
        setMessage('');
        setCategory('general');
        setSent(false);
        onOpenChange(false);
      }, 2000);
      
    } catch (err) {
      console.error('Support email error:', err);
      toast.error(err.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!sending) {
      setSubject('');
      setMessage('');
      setCategory('general');
      setSent(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg bg-slate-900 border border-cyan-500/30 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <MessageCircle className="w-5 h-5 text-emerald-400" />
            Contact Support
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Have a question, bug report, or feature request? We'd love to hear from you!
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="py-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mb-4" />
            <h3 className="text-lg font-bold text-white mb-2">Message Sent!</h3>
            <p className="text-slate-400">We'll get back to you as soon as possible.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Category</Label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white focus:border-cyan-500 focus:outline-none"
              >
                <option value="general">General Question</option>
                <option value="bug">Bug Report</option>
                <option value="feature">Feature Request</option>
                <option value="billing">Billing / Subscription</option>
                <option value="technical">Technical Issue</option>
                <option value="feedback">Feedback</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief summary of your message"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                disabled={sending}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Message</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Describe your question, issue, or feedback in detail..."
                rows={5}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 resize-none"
                disabled={sending}
              />
            </div>

            <div className="bg-slate-800/50 rounded-lg p-3 text-xs text-slate-400">
              <p className="flex items-center gap-2">
                <AlertCircle className="w-3 h-3" />
                Your email ({user?.email}) and current project info will be included to help us assist you better.
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={sending}
                className="border-slate-600"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sending || !subject.trim() || !message.trim()}
                className="bg-emerald-600 hover:bg-emerald-500"
              >
                {sending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Send Message</>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactSupportModal;
